import React, { useState, useEffect, useRef } from 'react';
import { invoicesAPI, suppliersAPI } from '../services/api'; // Added suppliersAPI
import './Modal.css';


function ReceptionReportSearchModal({ onClose, onLink, supplierId, supplierName }) {
    const [filters, setFilters] = useState({
        supplier_id: supplierId || '',
        date_from: '',
        date_to: '',
        ir_id: ''
    });

    // Autocomplete states
    // Initialize with name or ID to avoid useEffect clearing the filter on mount
    const [supplierSearch, setSupplierSearch] = useState(supplierName || (supplierId ? supplierId.toString() : ''));
    const [suppliers, setSuppliers] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    const searchTimeoutRef = useRef(null);
    const suggestionsRef = useRef(null);

    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Auto-search if supplierId is provided
    useEffect(() => {
        if (supplierId) {
            handleSearch();
            // Mostrar nombre si existe, sino el ID como fallback
            setSupplierSearch(supplierName || supplierId.toString());
        }
    }, [supplierId, supplierName]);

    // Autocomplete: Search suppliers
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Avoid searching if the current text matches exactly what was passed as default name
        // (This prevents initial load search spam if name is provided)
        if (supplierSearch === supplierName && supplierName) return;

        if (supplierSearch.length < 2) {
            setSuppliers([]);
            setShowSuggestions(false);

            // Should NOT clear filter if it matches the valid initial ID/Name state
            // Only clear if user explicitly cleared the input
            if (supplierSearch === '') {
                setFilters(prev => ({ ...prev, supplier_id: '' }));
            } else if (supplierSearch !== supplierName && supplierSearch !== (supplierId ? supplierId.toString() : '')) {
                // Update filter for short manual typing
                setFilters(prev => ({ ...prev, supplier_id: supplierSearch }));
            }
            return;
        }

        // Update filter with text immediately (for "part of name" search)
        setFilters(prev => ({ ...prev, supplier_id: supplierSearch }));

        setLoadingSuppliers(true);
        searchTimeoutRef.current = setTimeout(async () => {
            try {
                const response = await suppliersAPI.search(supplierSearch);
                setSuppliers(response.data || []);
                setShowSuggestions(true);
            } catch (err) {
                console.error('Error searching suppliers:', err);
            } finally {
                setLoadingSuppliers(false);
            }
        }, 300);

        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [supplierSearch]);

    // Click outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSupplierSelect = (supplier) => {
        setSupplierSearch(supplier.suName); // Show name in input
        setFilters(prev => ({ ...prev, supplier_id: supplier.suId })); // Set ID for precise search
        setShowSuggestions(false);
    };

    const handleSearch = async (e) => {
        if (e) e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (filters.supplier_id) params.supplier_id = filters.supplier_id;
            if (filters.date_from) params.date_from = filters.date_from.replace(/-/g, '');
            if (filters.date_to) params.date_to = filters.date_to.replace(/-/g, '');
            if (filters.ir_id) params.ir_id = filters.ir_id;

            const response = await invoicesAPI.searchReceptionReports(params);
            setResults(response.data || []);
        } catch (err) {
            setError(err.message || 'Error buscando informes');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return '-';

        // Si es YYYYMMDD (int o string numérico de 8 chars)
        const str = dateValue.toString();
        if (/^\d{8}$/.test(str)) {
            return `${str.substring(6, 8)}/${str.substring(4, 6)}/${str.substring(0, 4)}`;
        }

        // Si es una fecha ISO o Date object
        const d = new Date(dateValue);
        if (!isNaN(d.getTime())) {
            return d.toLocaleDateString('es-AR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }

        return dateValue;
    };

    const formatCurrency = (val) => {
        if (val === undefined || val === null) return '-';
        return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(val);
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{ width: '80%', maxWidth: '95%' }}>
                <div className="modal-header">
                    <h2>Vincular Informe de Recepción</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-body">
                    <form onSubmit={handleSearch} className="search-form" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
                        <div>
                            <label>ID Informe:</label>
                            <input
                                type="text"
                                value={filters.ir_id}
                                onChange={(e) => setFilters({ ...filters, ir_id: e.target.value })}
                                placeholder="Ej: 7133"
                                className="form-control"
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Proveedor (Nombre o ID):</label>
                            <div className="autocomplete-wrapper" ref={suggestionsRef}>
                                <input
                                    type="text"
                                    placeholder="Buscar proveedor..."
                                    value={supplierSearch}
                                    onChange={(e) => setSupplierSearch(e.target.value)}
                                    onFocus={() => {
                                        if (suppliers.length > 0) setShowSuggestions(true);
                                    }}
                                    className="autocomplete-input form-control"
                                    style={{ marginBottom: 0 }}
                                />
                                {loadingSuppliers && (
                                    <div className="autocomplete-loading">Buscando...</div>
                                )}
                                {showSuggestions && suppliers.length > 0 && (
                                    <div className="autocomplete-suggestions">
                                        {suppliers.map((supplier) => (
                                            <div
                                                key={supplier.suId}
                                                className="autocomplete-suggestion"
                                                onClick={() => handleSupplierSelect(supplier)}
                                            >
                                                <div className="suggestion-main">
                                                    <strong>{supplier.suName}</strong>
                                                    {supplier.suLegalNumber && (
                                                        <span className="suggestion-cuit">CUIT: {supplier.suLegalNumber}</span>
                                                    )}
                                                </div>
                                                {supplier.suLegalName && supplier.suLegalName !== supplier.suName && (
                                                    <div className="suggestion-secondary">{supplier.suLegalName}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div>
                            <label>Desde:</label>
                            <input
                                type="date"
                                value={filters.date_from}
                                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                                className="form-control"
                            />
                        </div>
                        <div>
                            <label>Hasta:</label>
                            <input
                                type="date"
                                value={filters.date_from}
                                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                                className="form-control"
                            />
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>
                                {loading ? 'Buscando...' : 'Buscar Informes'}
                            </button>
                        </div>
                    </form>

                    {error && <div className="alert alert-error">{error}</div>}

                    <div className="results-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Fecha</th>
                                    <th>Proveedor</th>
                                    <th>OCs</th>
                                    <th>Vencimiento</th>
                                    <th>Monto</th>
                                    <th>Estado</th>
                                    <th>Usuario</th>
                                    <th>Detalles</th>
                                    <th>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" style={{ textAlign: 'center' }}>
                                            {loading ? 'Cargando...' : 'No se encontraron resultados'}
                                        </td>
                                    </tr>
                                ) : (
                                    results.map(ir => (
                                        <tr key={ir.irHId}>
                                            <td>{ir.irHId}</td>
                                            <td>{formatDate(ir.irHDate)}</td>
                                            <td>{ir.supplier_name}</td>
                                            {/* OCs vinculadas */}
                                            <td>
                                                {ir.ocs && ir.ocs.map((oc, i) => (
                                                    <div key={i} style={{ fontSize: '0.9em' }}>
                                                        #{oc.id}
                                                    </div>
                                                ))}
                                            </td>
                                            {/* Vencimientos */}
                                            <td>
                                                {ir.ocs && ir.ocs.map((oc, i) => (
                                                    <div key={i} style={{ fontSize: '0.9em' }}>
                                                        {formatDate(oc.expiration)}
                                                    </div>
                                                ))}
                                            </td>
                                            {/* Montos */}
                                            <td>
                                                {ir.ocs && ir.ocs.map((oc, i) => (
                                                    <div key={i} style={{ fontSize: '0.9em' }}>
                                                        {formatCurrency(oc.amount)}
                                                    </div>
                                                ))}
                                            </td>
                                            {/* Estados */}
                                            <td>
                                                {ir.ocs && ir.ocs.map((oc, i) => (
                                                    <div key={i} style={{ fontSize: '0.9em' }}>
                                                        {oc.status || '-'}
                                                    </div>
                                                ))}
                                            </td>
                                            <td>{ir.irHUser}</td>
                                            <td>{ir.detail_count} líneas</td>
                                            <td>
                                                <button
                                                    className="btn btn-success btn-sm"
                                                    onClick={() => onLink(ir.irHId)}
                                                >
                                                    Vincular
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReceptionReportSearchModal;
